import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { FiX, FiCheck } from 'react-icons/fi';
import './ImageCrop.css';

/**
 * ImageCrop Component
 * Provides image cropping functionality
 */
const ImageCrop = ({ imageFile, onCropComplete, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);

  // Load image when component mounts
  React.useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  /**
   * Handle crop area change
   */
  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  /**
   * Handle zoom change
   */
  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  /**
   * Handle crop area complete
   */
  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  /**
   * Create cropped image blob
   */
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  /**
   * Get cropped image blob
   */
  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          return;
        }
        const file = new File([blob], imageFile.name, {
          type: imageFile.type || 'image/png',
        });
        resolve(file);
      }, imageFile.type || 'image/png');
    });
  };

  /**
   * Handle apply crop
   */
  const handleApply = async () => {
    if (!croppedAreaPixels || !imageSrc) {
      return;
    }

    try {
      const croppedImageFile = await getCroppedImg(imageSrc, croppedAreaPixels);
      if (croppedImageFile) {
        onCropComplete(croppedImageFile);
      }
    } catch (error) {
      console.error('Error cropping image:', error);
      alert('Failed to crop image. Please try again.');
    }
  };

  if (!imageSrc) {
    return (
      <div className="crop-modal-overlay">
        <div className="crop-modal">
          <div className="crop-loading">Loading image...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="crop-modal-overlay" onClick={onCancel}>
      <div className="crop-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crop-header">
          <h3>Crop Image</h3>
          <button className="crop-close-btn" onClick={onCancel} aria-label="Close">
            <FiX />
          </button>
        </div>

        <div className="crop-content-scroll">
          <div className="crop-container-wrapper">
            <div className="crop-container">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={undefined}
                onCropChange={onCropChange}
                onZoomChange={onZoomChange}
                onCropComplete={onCropCompleteCallback}
                cropShape="rect"
                showGrid={true}
                style={{
                  containerStyle: {
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    background: '#000',
                  },
                }}
              />
            </div>
          </div>

          <div className="crop-controls">
            <div className="zoom-control">
              <label>Zoom:</label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="zoom-slider"
              />
              <span className="zoom-value">{zoom.toFixed(1)}x</span>
            </div>
          </div>
        </div>

        <div className="crop-actions">
          <button className="crop-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="crop-apply-btn" onClick={handleApply}>
            <FiCheck /> Apply Crop
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCrop;


